import { Component, Input, OnInit, ViewChild } from "@angular/core";
import { Zone } from "@common/ADAPT.Common.Model/methodology/zone";
import { Configuration } from "@common/ADAPT.Common.Model/organisation/configuration";
import { RoleType } from "@common/ADAPT.Common.Model/organisation/role-type";
import { TeamLocation, TeamLocationBreezeModel } from "@common/ADAPT.Common.Model/organisation/team-location";
import { ActiveEntityUtilities } from "@common/lib/data/active-entity-utilities";
import { CommonDataService } from "@common/lib/data/common-data.service";
import { AdaptCommonDialogService } from "@common/ux/adapt-common-dialog/adapt-common-dialog.service";
import { IConfirmationDialogData } from "@common/ux/adapt-common-dialog/confirmation-dialog.component/confirmation-dialog.component";
import { ConfigureFeatureBase } from "@org-common/lib/configuration/base-components/configure-feature-base";
import { FeatureConfigurationComponent } from "@org-common/lib/configuration/base-components/feature-configuration/feature-configuration.component";
import { IConfigItem } from "@org-common/lib/configuration/configuration.interfaces";
import { ConfigurationService } from "@org-common/lib/configuration/configuration.service";
import { OrganisationService } from "@org-common/lib/organisation/organisation.service";
import { Tier1ArchitectureService } from "app/features/architecture/tier1-architecture/tier1-architecture.service";
import { forkJoin, lastValueFrom, of, switchMap } from "rxjs";

@Component({
    selector: "adapt-configure-tier1",
    templateUrl: "./configure-tier1.component.html",
    standalone: false,
})
export class ConfigureTier1Component extends ConfigureFeatureBase implements OnInit {
    @Input() public configItem?: IConfigItem;
    public roleType?: RoleType;
    public orgConfiguration?: Configuration;

    public productOnLeft!: boolean;
    public teamLocations: TeamLocation[] = [];
    public disablingTier1 = false;

    @ViewChild(FeatureConfigurationComponent) private featureConfigComponent?: FeatureConfigurationComponent;

    constructor(
        configurationService: ConfigurationService,
        private tier1Service: Tier1ArchitectureService,
        private orgService: OrganisationService,
        private commonDataService: CommonDataService,
        private dialogService: AdaptCommonDialogService,
    ) {
        super(configurationService);
    }

    public async initialiseData() {
        this.roleType = await lastValueFrom(this.tier1Service.getTier1RoleType());
        this.orgConfiguration = await lastValueFrom(this.orgService.getOrganisationConfiguration());
        this.productOnLeft = this.orgConfiguration.productZonePosition === 0;
        this.teamLocations = await lastValueFrom(this.commonDataService.getAll(TeamLocationBreezeModel));
    }

    public onZonePositionChange(value: boolean) {
        this.orgConfiguration!.productZonePosition = value ? 0 : 1;
    }

    public onFeatureChange(tier1Enabled: boolean) {
        this.disablingTier1 = !tier1Enabled;
        if (!tier1Enabled) {
            const teamsInKeyFunctions = this.teamLocations
                .filter((location) => location.keyFunctionId && ActiveEntityUtilities.isActive(location.team))
                .map((location) => location.team);
            if (teamsInKeyFunctions.length > 0) {
                const dialogData: IConfirmationDialogData = {
                    title: "Disabling Tier-1 architecture",
                    message: "<p>The following teams in key functions may be affected when you disable this feature:</p><ul>" +
                        teamsInKeyFunctions.map((team) => `<li>${team.name}</li>`).join("") +
                        "</ul><p>These teams will be moved to the designated zone.</p>",
                    confirmButtonText: "Yes, move teams",
                    cancelButtonText: "No, re-enable feature",
                };
                this.dialogService.openConfirmationDialogWithBoolean(dialogData).pipe(
                    switchMap((confirmed) => {
                        if (confirmed) {
                            // handle it here rather than onSave as saving may be perform through ChangeManager's "Save and Continue"
                            return this.handleTeamLocationsFromDisablingTier1();
                        }

                        return this.featureConfigComponent?.onFeatureStatusValueChanged(true) ?? of();
                    }),
                    this.takeUntilDestroyed(),
                ).subscribe();
            }
        } else {
            // just in case we disable and enable without saving
            this.commonDataService.rejectChanges(this.teamLocations).subscribe();
        }
    }

    private handleTeamLocationsFromDisablingTier1() {
        if (this.disablingTier1) {
            // Handle the case where Tier 1 is being disabled
            const keyFunctionLocations = this.teamLocations.filter((location) => location.keyFunction && ActiveEntityUtilities.isActive(location.team));
            const deletedLocations: TeamLocation[] = [];
            keyFunctionLocations.forEach((location) => {
                const destZone = location.keyFunction?.zone ??
                    (location.keyFunction?.valueStreamId ? Zone.EconomicEngine : undefined);
                if (destZone && !this.teamAlreadyInZone(location.teamId, destZone)) {
                    location.zone = destZone;
                    location.keyFunctionId = undefined;
                } else {
                    deletedLocations.push(location);
                }
            });

            if (deletedLocations.length > 0) {
                return forkJoin(deletedLocations.map((loc) => this.commonDataService.remove(loc)));
            }
        }

        return of();
    }

    private teamAlreadyInZone(teamId: number, zone: Zone) {
        return this.teamLocations.find((loc) => loc.teamId === teamId && loc.zone === zone);
    }
}
